import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";
import TeamResourceForm from "../Layouts/TeamResourceForm";
import { TeamResourceFormValues } from "src/models/TeamResource.model";
import {
  useGetTeamResourceQuery,
  useUpdateTeamResourceMutation,
} from "src/services/TeamResourceService";

type Props = {
  onClose: () => void;
  id: any;
};

const extensions: Record<string, string[]> = {
  IMAGE: ["JPEG", "JPG", "PNG", "GIF", "SVG"],
  PDF: ["PDF"],
  EXCEL: ["XLSX", "XLS", "CSV"],
};

const getFileType = (extension: string): string => {
  extension = extension.toUpperCase();
  for (const fileType in extensions) {
    if (extensions[fileType].includes(extension)) {
      return fileType;
    }
  }
  return "IMAGE";
};

const EditTeamResourceFormWrapper = ({ onClose, id }: Props) => {
  const [resourceData, setResourceData] = useState<any>({});

  const { data, isFetching, isLoading } = useGetTeamResourceQuery(id);

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setResourceData(data?.data);
    }
  }, [data, isLoading, isFetching]);

  const [updateTeamResource] = useUpdateTeamResourceMutation();

  // Form Initial Values
  const initialValues: TeamResourceFormValues = {
    visibility: resourceData?.visibility || "",
    fileUrl: resourceData?.fileUrl || "",
    title: resourceData?.title || "",
    sharedWithUserIds: resourceData?.sharedWithUserIds || [],
  };


  // Validation Schema
  const validationSchema = object().shape({
    fileUrl: string().required("Please upload file"),
    visibility: string().required("Please enter title "),
    title: string().required("Please enter title "),
  });

  // Handle Submit
  const handleSubmit = (
    values: TeamResourceFormValues,
    { setSubmitting, resetForm }: FormikHelpers<TeamResourceFormValues>
  ) => {
    const extension = values.fileUrl.split(".").pop()?.toUpperCase() || "";
    const fileType = getFileType(extension);
    const formattedValues = {
      title: values?.title,
      fileType: fileType,
      fileUrl: values?.fileUrl,
      visibility: values?.visibility,
      sharedWithUserIds: values?.sharedWithUserIds,
    };

    updateTeamResource({
      body: formattedValues,
      id: id,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {isLoading && isFetching && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <TeamResourceForm
              formikProps={formikProps}
              onClose={onClose}
              formType="UPDATE"
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditTeamResourceFormWrapper;
